<script setup lang="ts">
import { useSidebarWidthsStore } from "../../stores/useSidebarWidthsStore";
import {
  DEFAULT_SIDEBAR_WIDTH,
  type SidebarSide,
} from "../../stores/useSidebarWidthsStore.update";
import { isJust, just, nothing, toMaybe, type Maybe } from "../../lib/maybe";
const props = defineProps<{ side: SidebarSide }>();
const widths = useSidebarWidthsStore();
let drag: Maybe<{ x: number; width: number; unit: number }> = nothing;
function start(event: PointerEvent): void {
  if (
    event.button !== 0 ||
    event.currentTarget instanceof HTMLElement === false
  )
    return;
  const panel = toMaybe(event.currentTarget.parentElement);
  if (isJust(panel) === false) return;
  event.preventDefault();
  const unit = parseFloat(getComputedStyle(document.documentElement).fontSize);
  const scale =
    panel.value.getBoundingClientRect().width / panel.value.offsetWidth;
  drag = just({
    x: event.clientX,
    width: panel.value.offsetWidth / unit,
    unit: unit * scale,
  });
  event.currentTarget.setPointerCapture(event.pointerId);
}
function move(event: PointerEvent): void {
  if (isJust(drag) === false) return;
  const direction = props.side === "left" ? 1 : -1;
  void widths.dispatch({
    side: props.side,
    width:
      drag.value.width +
      (direction * (event.clientX - drag.value.x)) / drag.value.unit,
  });
}
function reset(): void {
  void widths.dispatch({ side: props.side, width: DEFAULT_SIDEBAR_WIDTH });
}
function keydown(event: KeyboardEvent): void {
  if (event.key === "Home" || event.key === "Enter") {
    event.preventDefault();
    reset();
  } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    event.preventDefault();
    const direction =
      (event.key === "ArrowRight" ? 1 : -1) * (props.side === "left" ? 1 : -1);
    void widths.dispatch({
      side: props.side,
      width: widths.widths[props.side] + direction * (event.shiftKey ? 2 : 0.5),
    });
  }
}
</script>
<template>
  <div
    class="sidebar-resize-handle"
    :class="`sidebar-resize-handle--${side}`"
    role="separator"
    aria-orientation="vertical"
    :aria-label="`Resize ${side} sidebar`"
    :aria-valuemin="14.5"
    :aria-valuemax="29"
    :aria-valuenow="widths.widths[side]"
    tabindex="0"
    title="Drag to resize. Double-click to reset."
    @pointerdown="start"
    @pointermove="move"
    @pointerup="drag = nothing"
    @pointercancel="drag = nothing"
    @lostpointercapture="drag = nothing"
    @dblclick="reset"
    @keydown="keydown"
  />
</template>
<style scoped>
.sidebar-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 6px;
  z-index: 5;
  cursor: col-resize;
  touch-action: none;
  user-select: none;
}
.sidebar-resize-handle--left {
  right: 0;
}
.sidebar-resize-handle--right {
  left: 0;
}
.sidebar-resize-handle::after {
  content: "";
  position: absolute;
  inset-block: 0;
  width: 1px;
  background: var(--color-zw-amber);
  opacity: 0;
  transition: opacity var(--duration-fast);
}
.sidebar-resize-handle--left::after {
  right: 0;
}
.sidebar-resize-handle--right::after {
  left: 0;
}
.sidebar-resize-handle:hover::after {
  opacity: 0.6;
}
.sidebar-resize-handle:focus-visible {
  outline: none;
}
.sidebar-resize-handle:focus-visible::after {
  background: var(--color-zw-amber);
  opacity: 1;
}
</style>
